import { useEffect, useState } from 'react';
import { X, Loader, Search, ExternalLink, Unlink } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';

interface SmartlinkResult {
  smartlink_id: string;
  slug: string;
  artist_name: string;
  release_name: string;
  smartlink_url: string;
}

interface Props {
  releaseId: string;
  releaseName: string | null;
  artistName: string | null;
  currentSmartlinkUrl: string | null;
  currentSmartlinkId: string | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

const FEEDBACKTOOL_SUPABASE_URL =
  (import.meta as any).env?.VITE_SUPABASE_URL || 'https://wrlgoxbzlngdtomjhvnz.supabase.co';
const FEEDBACKTOOL_ANON_KEY = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY || '';

export function LinkReleaseSmartlinkModal({
  releaseId,
  releaseName,
  artistName,
  currentSmartlinkUrl,
  currentSmartlinkId,
  onClose,
  onSaved,
}: Props) {
  const [autoLoading, setAutoLoading] = useState(false);
  const [autoChecked, setAutoChecked] = useState(false);
  const [autoMatch, setAutoMatch] = useState<SmartlinkResult | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<SmartlinkResult[]>([]);
  const [saving, setSaving] = useState(false);
  const [unlinking, setUnlinking] = useState(false);

  // Auto song.so lookup by artist + release name
  useEffect(() => {
    if (!artistName || !releaseName) return;
    let cancelled = false;
    (async () => {
      setAutoLoading(true);
      try {
        const resp = await fetch(`${FEEDBACKTOOL_SUPABASE_URL}/functions/v1/fetch-songso-smartlink`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${FEEDBACKTOOL_ANON_KEY}`,
            apikey: FEEDBACKTOOL_ANON_KEY,
          },
          body: JSON.stringify({ artist_name: artistName, release_name: releaseName }),
        });
        const data = await resp.json();
        if (cancelled) return;
        if (data?.found && data?.smartlink_url) {
          setAutoMatch({
            smartlink_id: data.smartlink_id || '',
            slug: data.slug || '',
            artist_name: data.artist_name || artistName,
            release_name: data.release_name || releaseName,
            smartlink_url: data.smartlink_url,
          });
        }
      } catch (err) {
        console.error('song.so auto-lookup failed:', err);
      } finally {
        if (!cancelled) {
          setAutoLoading(false);
          setAutoChecked(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [artistName, releaseName]);

  // Manual debounced search
  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const resp = await fetch(`${FEEDBACKTOOL_SUPABASE_URL}/functions/v1/fetch-songso-smartlink`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${FEEDBACKTOOL_ANON_KEY}`,
            apikey: FEEDBACKTOOL_ANON_KEY,
          },
          body: JSON.stringify({ query: q }),
        });
        const data = await resp.json();
        if (cancelled) return;
        setResults(Array.isArray(data?.results) ? data.results : []);
      } catch (err) {
        console.error('song.so search failed:', err);
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [searchQuery]);

  const link = async (r: SmartlinkResult) => {
    setSaving(true);
    try {
      const { error } = await (supabase as any)
        .from('release_campaign_links')
        .upsert(
          {
            release_id: releaseId,
            smartlink_url: r.smartlink_url,
            smartlink_id: r.smartlink_id || null,
          },
          { onConflict: 'release_id' },
        );
      if (error) throw error;
      toast.success('Smartlink linked');
      // Await parent refresh BEFORE closing so the divider flips to
      // 100% opacity in the same paint as the modal disappears.
      await onSaved();
      onClose();
    } catch (err) {
      console.error('Failed to link smartlink:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to link smartlink');
    } finally {
      setSaving(false);
    }
  };

  const unlink = async () => {
    setUnlinking(true);
    try {
      const { error } = await (supabase as any)
        .from('release_campaign_links')
        .update({ smartlink_url: null, smartlink_id: null })
        .eq('release_id', releaseId);
      if (error) throw error;
      toast.success('Smartlink unlinked');
      await onSaved();
      onClose();
    } catch (err) {
      console.error('Failed to unlink smartlink:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to unlink smartlink');
    } finally {
      setUnlinking(false);
    }
  };

  const isCurrent = (r: SmartlinkResult) => currentSmartlinkUrl === r.smartlink_url;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-xl w-full max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-base font-semibold dark:text-white flex items-center gap-2">
            <span className="w-5 h-5 rounded-sm bg-black flex items-center justify-center text-white text-[10px] font-bold">s.</span>
            Link song.so smartlink
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-3 overflow-y-auto flex-1">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            Release: <span className="font-medium text-gray-700 dark:text-gray-200">{artistName || '?'} — {releaseName || '?'}</span>
          </p>

          {currentSmartlinkUrl && (
            <div className="mb-4 p-3 rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
              <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Currently linked</div>
              <div className="flex items-center justify-between gap-2">
                <a
                  href={currentSmartlinkUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline break-all flex items-center gap-1"
                >
                  <ExternalLink className="h-3 w-3 shrink-0" />
                  {currentSmartlinkUrl}
                </a>
                <button
                  onClick={unlink}
                  disabled={unlinking}
                  className="shrink-0 inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-red-50 hover:bg-red-100 dark:bg-red-900/30 dark:hover:bg-red-900/50 text-red-700 dark:text-red-300 disabled:opacity-50"
                >
                  {unlinking ? <Loader className="h-3 w-3 animate-spin" /> : <Unlink className="h-3 w-3" />}
                  Unlink
                </button>
              </div>
              {currentSmartlinkId && (
                <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">id: {currentSmartlinkId}</div>
              )}
            </div>
          )}

          {/* Auto-detected suggestion */}
          {autoLoading ? (
            <div className="mb-4 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              <Loader className="h-3 w-3 animate-spin" />
              Searching song.so for &ldquo;{artistName} — {releaseName}&rdquo;…
            </div>
          ) : autoMatch ? (
            <div className="mb-4">
              <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Auto-matched on song.so</div>
              <button
                onClick={() => link(autoMatch)}
                disabled={saving || isCurrent(autoMatch)}
                className={`w-full text-left p-3 rounded border transition-colors ${
                  isCurrent(autoMatch)
                    ? 'border-blue-300 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-700 cursor-default'
                    : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                } disabled:opacity-50`}
              >
                <div className="text-sm font-medium dark:text-white">
                  {autoMatch.artist_name} — {autoMatch.release_name}
                </div>
                <div className="text-xs text-blue-600 dark:text-blue-400 break-all mt-1">{autoMatch.smartlink_url}</div>
                {isCurrent(autoMatch) && <div className="mt-1 text-[11px] text-blue-700 dark:text-blue-300">Already linked</div>}
              </button>
            </div>
          ) : autoChecked ? (
            <div className="mb-4 text-xs text-amber-600 dark:text-amber-400">
              No automatic match on song.so. Search manually below.
            </div>
          ) : null}

          {/* Manual search */}
          <div className="relative mb-2">
            <Search className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search song.so manually…"
              className="w-full pl-8 pr-3 py-2 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
            />
          </div>
          {searching && (
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mt-1">
              <Loader className="h-3 w-3 animate-spin" /> Searching…
            </div>
          )}
          {!searching && results.length > 0 && (
            <ul className="mt-2 space-y-1 max-h-72 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded">
              {results.map((r) => (
                <li key={r.smartlink_id || r.smartlink_url}>
                  <button
                    onClick={() => link(r)}
                    disabled={saving || isCurrent(r)}
                    className={`w-full text-left px-3 py-2 text-xs transition-colors ${
                      isCurrent(r)
                        ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                    } disabled:opacity-50`}
                  >
                    <div className="font-medium">{r.artist_name} — {r.release_name}</div>
                    <div className="text-gray-500 dark:text-gray-400 break-all">{r.smartlink_url}</div>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {!searching && searchQuery.trim().length >= 2 && results.length === 0 && (
            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">No results.</div>
          )}
        </div>
      </div>
    </div>
  );
}
